import { useMediaQuery } from "@mui/material";
import React, { useId } from "react";
import { theme } from "@/theme";
import { productSelect, ScentDetails } from "@/store";
import { Card } from "@/components/Card";
import { SizedImage } from "@/components/SizedImage";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { OneTimeIcon } from "@/components/icons";
import { ProductCardProps } from "./ProductCard";
import { MediaGallery } from "@/features/pdp/media-gallery";
import { usePdpFocusedProduct } from "@/features/pdp/PdpContext";
import { ProductBadge } from "./ProductBadge";

export const ProductCardCombined = (props: ProductCardProps) => {
  const {
    productCardValue,
    productTitle,
    truncateText = false,
    compact,
    cover,
    actions,
    isSubBadge,
    showRatings = false,
    quickAddButtons,
    hoverImagesOnly = false,
    showFeaturedText = true,
    showProductCardDescription = true,
    ...cardProps
  } = props;

  const product = usePdpFocusedProduct();
  const cardContentId = useId();

  const isSmallScreen = useMediaQuery(
    theme.breakpoints.down(theme.breakpoints.values.md)
  );

  const scents =
    productCardValue.collectionProducts
      ?.map((p) => p.scent)
      .filter((scent): scent is ScentDetails => scent != null) ?? [];

  let subBadgeUi = undefined;
  if (isSubBadge !== null && isSubBadge !== undefined) {
    subBadgeUi = (
      <div className="!-mt-4 flex justify-end">
        <div className="-mb-4 -mr-4 flex items-center rounded-tl-lg bg-primary-50 px-2 py-2 text-xs text-primary">
          {isSubBadge ? (
            <>
              <AutorenewIcon sx={{ fontSize: "1.5em" }} />
              <span className="pl-1 ">Subscription</span>
            </>
          ) : (
            <>
              <OneTimeIcon sx={{ fontSize: "1.5em" }} />
              <span className="pl-1">One Time</span>
            </>
          )}
        </div>
      </div>
    );
  }

  const productCardImage =
    productCardValue?.featuredImage?.src === undefined ||
    productCardValue?.featuredImage?.src === "" ? null : compact ? (
      <div className="relative w-20 shrink-0 [&>img]:max-w-full">
        <SizedImage
          src={productCardValue.featuredImage.src}
          alt={productCardValue.title}
          height={160}
          width={160}
          scaleFactor={2}
          loading="lazy"
        />
        {productCardValue.badge &&
          (productCardValue.badge.text || productCardValue.badge.image) &&
          (productCardValue.badge.text ? (
            <ProductBadge text={productCardValue.badge.text} />
          ) : (
            <ProductBadge
              image={productCardValue.badge.image}
              position={productCardValue.badge.position}
              width={productCardValue.badge.width}
              text={productCardValue.badge.text}
            />
          ))}
      </div>
    ) : (
      <>
        <div className="relative">
          <a
            href={productSelect.url(product)}
            aria-label={`Shop ${product.title}`}
            aria-describedby={cardContentId}
            className="flex flex-1"
          >
            <div>
              <MediaGallery
                mode={hoverImagesOnly ? "hoverSwap" : "carousel"}
                dots={"none"}
                imageLoading={productCardValue.featuredImage.loading}
              />
            </div>
          </a>
          {productCardValue.badge &&
            (productCardValue.badge.text || productCardValue.badge.image) &&
            (productCardValue.badge.text ? (
              <ProductBadge text={productCardValue.badge.text} />
            ) : (
              <ProductBadge
                image={productCardValue.badge.image}
                position={productCardValue.badge.position}
                width={productCardValue.badge.width}
                text={productCardValue.badge.text}
              />
            ))}
          {quickAddButtons}
        </div>
      </>
    );

  const productCardContent = (
    <div
      id={cardContentId}
      className={`scent-consolidation-card relative flex h-full gap-4 ${
        compact ? "flex-row space-x-4" : "flex-col"
      }`}
    >
      {cover === undefined ? productCardImage : cover || null}
      <a
        href={productSelect.url(product)}
        aria-label={`Shop ${product.title}`}
        aria-describedby={cardContentId}
        className={`flex-1`}
      >
        <div
          data-id="product-card-content"
          className={`flex grow flex-col gap-4 text-left ${
            compact ? "basis-9/12" : ""
          }`}
        >
          <div>
            {showRatings && (
              <div data-id="product-card-ratings">
                <div
                  data-bv-show="inline_rating"
                  data-bv-product-id={product?.id}
                  data-bv-seo="false"
                  style={{ minHeight: "22px" }}
                ></div>
              </div>
            )}
            {productTitle ?? (
              <h3
                data-id="product-card-title"
                className={`title text-md lg:text-xl`}
              >
                {productSelect
                  .name(productCardValue)
                  .replace(/\b(biobased|power)\b/gi, "")
                  .trim()}
              </h3>
            )}

            {scents.length > 0 && (
              <div className="group-hover-hidden mt-2 flex items-center space-x-1.5">
                {scents.map((scent, i) => (
                  <div
                    className="h-4 w-4 rounded-full ring-1 ring-primary"
                    style={{ backgroundColor: scent.color }}
                    key={`${scent.name}-${i}`}
                  />
                ))}
                <span className="body text-sm">
                  {scents.length === 1
                    ? scents[0].name
                    : `${scents.length} Scents`}
                </span>
              </div>
            )}
          </div>
          {showFeaturedText && productCardValue.featuredText && (
            <>
              {!compact && (
                <hr
                  data-id="product-card-divider"
                  className="divider-x mb-0 mt-0 hidden lg:block"
                />
              )}
              <p
                data-id="product-card-featured-text"
                className={`group-hover-hidden mb-0 text-xxs sm:text-md`}
              >
                {productCardValue.featuredText}
              </p>
            </>
          )}
          {showProductCardDescription &&
            productCardValue.product?.productCardDescription && (
              <>
                {!compact && (
                  <hr
                    data-id="product-card-divider"
                    className="divider-x group-hover-hidden mb-0 mt-0 hidden lg:block"
                  />
                )}
                <p
                  data-id="product-card-featured-text"
                  className={`group-hover-hidden mb-0 text-xxs sm:text-md`}
                >
                  {productCardValue.product.productCardDescription}
                </p>
              </>
            )}

          {(productCardValue?.size ||
            productCardValue?.subscription ||
            productCardValue?.price) && (
            <div className="group-hover-hidden flex flex-1 items-end justify-between space-x-2">
              {(productCardValue?.size || productCardValue?.subscription) && (
                <div>
                  {productCardValue?.size && (
                    <p
                      data-id="product-card-size"
                      className={`body text-sm ${
                        compact ? "" : "sm:text-base"
                      }`}
                    >
                      {productCardValue.size}
                    </p>
                  )}
                  {productCardValue?.subscription && (
                    <p
                      data-id="product-card-subscription"
                      className={`body text-sm ${
                        compact ? "" : "sm:text-base"
                      }`}
                    >
                      {productCardValue.subscription}
                    </p>
                  )}
                </div>
              )}
              {productCardValue?.price != undefined ? (
                <div className="flex flex-wrap items-center justify-end space-x-2">
                  <p
                    data-id="product-card-price"
                    className={`body text-sm font-semibold ${
                      compact ? "" : "sm:text-lg"
                    } ${
                      productCardValue?.size || productCardValue?.subscription
                        ? "order-2 ml-2"
                        : ""
                    }`}
                  >
                    <span className="sr-only">Current price</span>
                    {productCardValue.price.format()}
                  </p>
                  {productCardValue?.compareAtPrice != undefined ? (
                    <p
                      data-id="product-card-compare-at-price"
                      className={`body text-sm text-gray line-through ${
                        productCardValue?.size || productCardValue?.subscription
                          ? "order-1"
                          : "ml-2"
                      }`}
                    >
                      <span className="sr-only">Regular price</span>
                      {productCardValue.compareAtPrice.format()}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          )}
        </div>
      </a>
    </div>
  );

  return (
    <Card
      data-id="product-card"
      {...cardProps}
      actions={actions && <Card.Actions>{actions}</Card.Actions>}
      hoverable={cardProps.hoverable ?? !isSmallScreen}
      bordered={cardProps.bordered ?? false}
    >
      {productCardContent}
      {subBadgeUi}
    </Card>
  );
};
